import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Checkbox, Chip,
  CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  FormGroup, FormLabel, Grid, IconButton, MenuItem, Paper, Select, Stack, Switch, TextField, Typography,
  createFilterOptions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { PERMISSIONS } from "../../constants";
import {
  createTrdDraft, discardTrdDraft, downloadTrdXlsx, getTrdVersion, listTrdDocumentTypes, listTrdVersions,
  publishTrdVersion, saveTrdDraft,
} from "../../services/backend/TrdService";
import {
  STATUS_COLORS, STATUS_LABELS, SUPPORT_LABELS, describeErrors, emptySerie, emptySubserie, trdError,
  typeKey,
} from "./trdLabels";

const yearsValue = (v) => (v === null || v === undefined ? "" : v);
const toYears = (text) => (text === "" ? null : Number(text));

const toggle = (list, value) => (list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

// A new type is offered as "Crear «…»" when what was typed matches none.
const NEW_TYPE = "\u0000nuevo:";
const filter = createFilterOptions();
const labelOf = (option) => (option.startsWith(NEW_TYPE) ? option.slice(NEW_TYPE.length) : option);

// The document types of a subserie: pick from the shared list or create one.
function FiletypesField({ value, options, readOnly, onChange }) {
  return (
    <Autocomplete
      multiple freeSolo disabled={readOnly} value={value} options={options}
      filterOptions={(opts, params) => {
        const filtered = filter(opts.filter((o) => !value.includes(o)), params);
        const typed = params.inputValue.trim();
        if (typed && !opts.some((o) => typeKey(o) === typeKey(typed))) filtered.push(`${NEW_TYPE}${typed}`);
        return filtered;
      }}
      getOptionLabel={labelOf}
      renderOption={(props, option) => (
        <li {...props} key={option}>
          {option.startsWith(NEW_TYPE) ? `Crear «${labelOf(option)}»` : option}
        </li>
      )}
      onChange={(_, next) => {
        const seen = new Set();
        onChange(next.map((v) => labelOf(String(v)).trim()).filter((v) => {
          const key = typeKey(v);
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        }));
      }}
      renderTags={(tags, getTagProps) => tags.map((option, index) => (
        <Chip size="small" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
      ))}
      renderInput={(params) => (
        <TextField
          {...params} size="small" label="Tipos documentales"
          helperText="Elige uno de la lista o escribe uno nuevo y pulsa Enter para crearlo"
        />
      )}
    />
  );
}

function SubserieForm({ sub, readOnly, typeOptions, onChange, onRemove }) {
  const set = (patch) => onChange({ ...sub, ...patch });
  const setRetention = (patch) => set({ retention: { ...sub.retention, ...patch } });
  const setDisposition = (patch) => set({ final_disposition: { ...sub.final_disposition, ...patch } });
  return (
    <Accordion disableGutters variant="outlined" sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 500 }}>
          {sub.code || "Sin código"} — {sub.name || "Subserie sin nombre"}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" label="Código" value={sub.code} disabled={readOnly}
              onChange={(e) => set({ code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              fullWidth size="small" label="Subserie" value={sub.name} disabled={readOnly}
              onChange={(e) => set({ name: e.target.value })}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth size="small" type="number" label="Retención en archivo de gestión (años)"
              inputProps={{ min: 0, step: 1 }} disabled={readOnly}
              value={yearsValue(sub.retention.management)}
              onChange={(e) => setRetention({ management: toYears(e.target.value) })}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth size="small" type="number" label="Retención en archivo central (años)"
              inputProps={{ min: 0, step: 1 }} disabled={readOnly}
              value={yearsValue(sub.retention.central)}
              onChange={(e) => setRetention({ central: toYears(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              label="Conservación total" disabled={readOnly}
              control={(
                <Checkbox
                  size="small" checked={sub.final_disposition.total_conservation}
                  onChange={(e) => setDisposition({
                    total_conservation: e.target.checked,
                    ...(e.target.checked ? { selection: null } : {}),
                  })}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" type="number" label="Selección (años)"
              inputProps={{ min: 0, step: 1 }}
              disabled={readOnly || sub.final_disposition.total_conservation}
              value={yearsValue(sub.final_disposition.selection)}
              onChange={(e) => setDisposition({ selection: toYears(e.target.value) })}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={2} size="small" label="Procedimiento" disabled={readOnly}
              value={sub.procedure} onChange={(e) => set({ procedure: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <FiletypesField
              value={sub.filetypes} options={typeOptions} readOnly={readOnly}
              onChange={(filetypes) => set({ filetypes })}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <FormLabel component="legend" sx={{ fontSize: ".8em" }}>Soporte (opcional)</FormLabel>
            <FormGroup row>
              {Object.entries(SUPPORT_LABELS).map(([value, label]) => (
                <FormControlLabel
                  key={value} label={label} disabled={readOnly}
                  control={<Checkbox size="small" checked={sub.soporte.includes(value)} onChange={() => set({ soporte: toggle(sub.soporte, value) })} />}
                />
              ))}
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth multiline size="small" label="Observaciones" disabled={readOnly}
              value={sub.observaciones} onChange={(e) => set({ observaciones: e.target.value })}
            />
          </Grid>
          {!readOnly && (
            <Grid item xs={12}>
              <Button color="error" size="small" startIcon={<DeleteOutlineIcon />} onClick={onRemove}>
                Quitar subserie
              </Button>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

function SerieForm({ serie, readOnly, typeOptions, onChange, onRemove }) {
  const setSub = (i, next) =>
    onChange({ ...serie, subseries: serie.subseries.map((s, k) => (k === i ? next : s)) });
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth size="small" label="Código de serie" value={serie.code} disabled={readOnly}
            onChange={(e) => onChange({ ...serie, code: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={readOnly ? 6 : 5}>
          <TextField
            fullWidth size="small" label="Serie" value={serie.name} disabled={readOnly}
            onChange={(e) => onChange({ ...serie, name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth size="small" label="Código de formato" placeholder="P01-F01" value={serie.format_code || ""}
            disabled={readOnly} onChange={(e) => onChange({ ...serie, format_code: e.target.value })}
          />
        </Grid>
        {!readOnly && (
          <Grid item xs={12} sm={1}>
            <IconButton color="error" onClick={onRemove} aria-label="Quitar serie"><DeleteOutlineIcon /></IconButton>
          </Grid>
        )}
      </Grid>
      <Box sx={{ mt: 2 }}>
        {serie.subseries.map((sub, i) => (
          <SubserieForm
            key={i} sub={sub} readOnly={readOnly} typeOptions={typeOptions}
            onChange={(next) => setSub(i, next)}
            onRemove={() => onChange({ ...serie, subseries: serie.subseries.filter((_, k) => k !== i) })}
          />
        ))}
        {!readOnly && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => onChange({ ...serie, subseries: [...serie.subseries, emptySubserie()] })}>
            Agregar subserie
          </Button>
        )}
      </Box>
    </Paper>
  );
}

const draftOf = (v) => ({
  name: v.name,
  table_number: v.table_number === undefined ? null : v.table_number,
  active: v.active,
  series: v.series.map((s) => ({
    code: s.code, name: s.name, format_code: s.format_code || "",
    subseries: s.subseries.map((b) => {
      const blank = emptySubserie();
      return {
        ...blank, ...b,
        soporte: b.soporte || [],
        retention: { ...blank.retention, ...(b.retention || {}) },
        final_disposition: { ...blank.final_disposition, ...(b.final_disposition || {}) },
        filetypes: b.filetypes || [],
      };
    }),
  })),
});

// Una dependencia de la TRD: sus versiones y el árbol serie → subserie con
// todos los parámetros. Solo un borrador se edita; lo vigente se consulta y,
// para cambiarlo, se crea una versión nueva.
export default function TrdEditor() {
  const { dependenceCode } = useParams();
  const code = decodeURIComponent(dependenceCode);
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const can = (p) => Array.isArray(user?.permissions) && user.permissions.includes(p);

  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState(null); // the document shown
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [problems, setProblems] = useState([]);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [approval, setApproval] = useState({ actNumber: "", actDate: "", issuingBody: "", convalidation: "" });

  const show = useCallback((v) => {
    setVersion(v);
    setDraft(draftOf(v));
    setDirty(false);
  }, []);

  const load = useCallback(async (number) => {
    setLoading(true);
    const [list, one] = await Promise.all([listTrdVersions(code), getTrdVersion(code, number)]);
    setLoading(false);
    if (list.error || one.error) { setError(trdError(one.error || list.error)); return; }
    setVersions(list.body.versions);
    show(one.body.version);
  }, [code, show]);

  useEffect(() => { load(); }, [load]);

  // The shared list of document types; failing to get it only costs the
  // suggestions, a type can still be typed.
  useEffect(() => {
    listTrdDocumentTypes().then(({ body }) => {
      if (body?.types) setCatalog(body.types.map((t) => t.name));
    });
  }, []);

  const readOnly = !version || version.status !== "BORRADOR" || !can(PERMISSIONS.TRD.UPDATE);
  const hasDraft = versions.some((v) => v.status === "BORRADOR");
  const current = versions.find((v) => v.status === "VIGENTE");

  const fail = (res) => {
    setError(trdError(res.error));
    setProblems(describeErrors(res.errors, draft));
    // The page is long: bring the message into view.
    if (typeof window.scrollTo === "function") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async () => {
    setBusy(true); setError(null); setProblems([]); setNotice(null);
    const res = await saveTrdDraft(code, version.version, draft);
    setBusy(false);
    if (res.error) { fail(res); return false; }
    show(res.body.version);
    setNotice("Borrador guardado.");
    return true;
  };

  const handleNewDraft = async () => {
    setBusy(true); setError(null); setProblems([]);
    const res = await createTrdDraft(code);
    setBusy(false);
    if (res.error) { fail(res); return; }
    await load(res.body.version.version);
  };

  const handleDiscard = async () => {
    if (!window.confirm("¿Descartar este borrador? Se perderán sus cambios.")) return;
    setBusy(true); setError(null); setProblems([]);
    const res = await discardTrdDraft(code, version.version);
    setBusy(false);
    if (res.error) { fail(res); return; }
    if (versions.length <= 1) { navigate("/trd"); return; }
    await load();
  };

  const handlePublish = async (evt) => {
    evt.preventDefault();
    setBusy(true); setError(null); setProblems([]);
    // What is on screen is what gets published.
    if (dirty && !(await handleSave())) { setBusy(false); setPublishing(false); return; }
    setPublishError(null);
    const res = await publishTrdVersion(code, version.version, approval);
    setBusy(false);
    // Stay in the dialog: the reason is shown right where the archivist is.
    if (res.error) {
      setPublishError({ message: trdError(res.error), problems: describeErrors(res.errors, draft) });
      return;
    }
    setPublishError(null);
    setPublishing(false);
    setNotice("Versión publicada. Ya es la vigente en Expedientes.");
    await load(res.body.version.version);
  };

  const handleExport = async () => {
    const err = await downloadTrdXlsx(code, version?.version);
    if (err) setError(trdError(err));
  };

  const pending = useMemo(() => version?.pendingSubseries || 0, [version]);

  // The shared list plus whatever is in this draft, so a type created in one
  // subserie can be chosen in the next without saving first.
  const typeOptions = useMemo(() => {
    const names = new Map(catalog.map((n) => [typeKey(n), n]));
    (draft?.series || []).forEach((s) => s.subseries.forEach((b) => b.filetypes.forEach((n) => {
      if (!names.has(typeKey(n))) names.set(typeKey(n), n);
    })));
    return [...names.values()];
  }, [catalog, draft]);

  if (loading && !version) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  }
  if (!version) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/trd")} sx={{ mt: 2 }}>Volver</Button>
      </Container>
    );
  }

  const setSerie = (i, next) => { setDraft({ ...draft, series: draft.series.map((s, k) => (k === i ? next : s)) }); setDirty(true); };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/trd")} sx={{ mb: 1 }}>Todas las dependencias</Button>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="h4">{code} — {draft.name}</Typography>
        <Chip color={STATUS_COLORS[version.status]} label={`${STATUS_LABELS[version.status]} v${version.version}`} />
        {pending > 0 && <Chip color="warning" label={`${pending} subserie${pending === 1 ? "" : "s"} con parámetros pendientes`} />}
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <Select size="small" value={version.version} onChange={(e) => load(e.target.value)}>
          {versions.map((v) => (
            <MenuItem key={v.version} value={v.version}>
              v{v.version} — {STATUS_LABELS[v.status]}{v.imported ? " (importada)" : ""}
            </MenuItem>
          ))}
        </Select>
        {version.approval && (
          <Typography variant="body2" color="text.secondary">
            Acto {version.approval.actNumber} del {version.approval.actDate} — {version.approval.issuingBody}
          </Typography>
        )}
        {version.imported && !version.approval && (
          <Typography variant="body2" color="text.secondary">Importada de la TRD existente, sin acto administrativo registrado.</Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {can(PERMISSIONS.TRD.EXPORT)
          && <Button startIcon={<FileDownloadIcon />} onClick={handleExport}>Exportar</Button>}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null); setProblems([]); }}>
        {error}
        {problems.length > 0 && <Box component="ul" sx={{ m: 0, pl: 3 }}>{problems.map((p, i) => <li key={i}>{p}</li>)}</Box>}
      </Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      {version.status !== "BORRADOR" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Esta versión no se edita.
          {version.status === "VIGENTE" && !hasDraft && can(PERMISSIONS.TRD.CREATE)
            && " Para cambiarla, crea una versión nueva: parte de esta y la vigente no cambia hasta que se publique."}
          {hasDraft && " La dependencia ya tiene un borrador abierto."}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Dependencia (oficina productora)" value={draft.name} disabled={readOnly}
              onChange={(e) => { setDraft({ ...draft, name: e.target.value }); setDirty(true); }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" type="number" label="N.º de tabla" inputProps={{ min: 0, step: 1 }}
              disabled={readOnly} value={yearsValue(draft.table_number)}
              onChange={(e) => { setDraft({ ...draft, table_number: toYears(e.target.value) }); setDirty(true); }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              label="Activa" disabled={readOnly}
              control={<Switch checked={draft.active} onChange={(e) => { setDraft({ ...draft, active: e.target.checked }); setDirty(true); }} />}
            />
          </Grid>
        </Grid>
      </Paper>

      {draft.series.map((s, i) => (
        <SerieForm
          key={i} serie={s} readOnly={readOnly} typeOptions={typeOptions}
          onChange={(next) => setSerie(i, next)}
          onRemove={() => { setDraft({ ...draft, series: draft.series.filter((_, k) => k !== i) }); setDirty(true); }}
        />
      ))}
      {draft.series.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>Esta dependencia aún no tiene series.</Alert>}

      {!readOnly && (
        <Button startIcon={<AddIcon />} sx={{ mb: 3 }} onClick={() => { setDraft({ ...draft, series: [...draft.series, emptySerie()] }); setDirty(true); }}>
          Agregar serie
        </Button>
      )}

      <Stack direction="row" spacing={1}>
        {version.status === "BORRADOR" && can(PERMISSIONS.TRD.UPDATE)
          && <Button variant="outlined" onClick={handleSave} disabled={busy || !dirty}>Guardar borrador</Button>}
        {version.status === "BORRADOR" && can(PERMISSIONS.TRD.PUBLISH)
          && <Button variant="contained" onClick={() => { setPublishError(null); setPublishing(true); }} disabled={busy}>Publicar…</Button>}
        {version.status === "BORRADOR" && can(PERMISSIONS.TRD.UPDATE)
          && <Button color="error" onClick={handleDiscard} disabled={busy}>Descartar borrador</Button>}
        {!hasDraft && current && can(PERMISSIONS.TRD.CREATE)
          && <Button variant="contained" onClick={handleNewDraft} disabled={busy}>Crear versión nueva</Button>}
      </Stack>

      <Dialog open={publishing} onClose={() => setPublishing(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handlePublish}>
          <DialogTitle>Publicar versión {version.version}</DialogTitle>
          <DialogContent>
            {publishError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {publishError.message}
                {publishError.problems.length > 0 && (
                  <Box component="ul" sx={{ m: 0, pl: 3 }}>{publishError.problems.map((p, i) => <li key={i}>{p}</li>)}</Box>
                )}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Pasa a ser la versión vigente y reemplaza a la actual en Expedientes. Lo ya archivado conserva la
              clasificación con la que se creó.
            </Typography>
            <TextField
              required fullWidth margin="dense" label="Número del acto administrativo" value={approval.actNumber}
              onChange={(e) => setApproval({ ...approval, actNumber: e.target.value })}
            />
            <TextField
              required fullWidth margin="dense" type="date" label="Fecha del acto" InputLabelProps={{ shrink: true }}
              value={approval.actDate} onChange={(e) => setApproval({ ...approval, actDate: e.target.value })}
            />
            <TextField
              required fullWidth margin="dense" label="Órgano que lo expide" value={approval.issuingBody}
              onChange={(e) => setApproval({ ...approval, issuingBody: e.target.value })}
            />
            <TextField
              fullWidth margin="dense" label="Convalidación (opcional)" value={approval.convalidation}
              onChange={(e) => setApproval({ ...approval, convalidation: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPublishing(false)}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={busy}>Publicar</Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Container>
  );
}
